import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Coffee, ShoppingCart, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMenu } from '../contexts/MenuContext';
import { toast } from 'react-hot-toast';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, logout } = useAuth();
  const { getCartItemCount } = useMenu();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMenuOpen]);

  return (
    <nav className="bg-black text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Logo size="sm" className="h-7 w-7" />
            <span className="text-lg font-bold whitespace-nowrap">Kiss Coffee Minehead</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium hover:text-red-500 transition-colors ${
                isActive('/') ? 'text-red-500' : ''
              }`}
            >
              Home
            </Link>
            <Link
              to="/menu"
              className={`text-sm font-medium hover:text-red-500 transition-colors ${
                isActive('/menu') ? 'text-red-500' : ''
              }`}
            >
              Menu
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium hover:text-red-500 transition-colors ${
                isActive('/about') ? 'text-red-500' : ''
              }`}
            >
              About Us
            </Link>
            <Link
              to="/order"
              className={`text-sm font-medium hover:text-red-500 transition-colors flex items-center space-x-1 ${
                isActive('/order') ? 'text-red-500' : ''
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              <span>Order</span>
              {getCartItemCount() > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getCartItemCount()}
                </span>
              )}
            </Link>

            {/* Admin Links */}
            {isAdmin && (
              <div className="flex items-center space-x-4 border-l border-gray-600 pl-4">
                <Link
                  to="/admin"
                  className={`hover:text-red-500 transition-colors ${
                    isActive('/admin') ? 'text-red-500' : ''
                  }`}
                >
                  Admin
                </Link>
                <Link
                  to="/admin/orders"
                  className={`hover:text-red-500 transition-colors ${
                    isActive('/admin/orders') ? 'text-red-500' : ''
                  }`}
                >
                  Orders
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 hover:text-red-500 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation (fixed overlay panel below navbar) */}
        {isMenuOpen && (
          <div
            className="md:hidden absolute inset-x-0 top-full z-50 bg-black py-4 px-4"
          >
            <div className="flex flex-col space-y-3">
              <Link
                to="/"
                className={`hover:text-red-500 transition-colors py-2 ${
                  isActive('/') ? 'text-red-500' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/menu"
                className={`hover:text-red-500 transition-colors py-2 ${
                  isActive('/menu') ? 'text-red-500' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Menu
              </Link>
              <Link
                to="/about"
                className={`hover:text-red-500 transition-colors py-2 ${
                  isActive('/about') ? 'text-red-500' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              <Link
                to="/order"
                className={`hover:text-red-500 transition-colors flex items-center space-x-2 py-2 ${
                  isActive('/order') ? 'text-red-500' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <ShoppingCart className="h-4 w-4" />
                <span>Order</span>
                {getCartItemCount() > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getCartItemCount()}
                  </span>
                )}
              </Link>

              {/* Mobile Admin Links */}
              {isAdmin && (
                <div className="pt-3 border-t border-gray-600 space-y-3">
                  <Link
                    to="/admin"
                    className={`block hover:text-red-500 transition-colors py-2 ${
                      isActive('/admin') ? 'text-red-500' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Admin Dashboard
                  </Link>
                  <Link
                    to="/admin/orders"
                    className={`block hover:text-red-500 transition-colors py-2 ${
                      isActive('/admin/orders') ? 'text-red-500' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Order Panel
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 hover:text-red-500 transition-colors py-2 w-full"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Tap-outside overlay to close menu on mobile/tablet */}
        {isMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40"
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />
        )}
      </div>
    </nav>
  );
};

export default Navbar;

